import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, Trash2, X, FileText, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { notificationAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationTarget } from '../utils/notificationLink';

const NOTIFICATION_ICONS = {
  document_uploaded: FileText,
  document_approved: CheckCircle,
  document_rejected: XCircle,
  case_updated: AlertTriangle,
  payment_received: CheckCircle,
  payment_due: AlertTriangle,
  complaint_filed: AlertTriangle,
  complaint_resolved: CheckCircle,
  agreement_signed: FileText,
  system: Bell,
};

const NOTIFICATION_COLORS = {
  document_uploaded: 'bg-blue-100 text-blue-600',
  document_approved: 'bg-green-100 text-green-600',
  document_rejected: 'bg-red-100 text-red-600',
  case_updated: 'bg-amber-100 text-amber-600',
  payment_received: 'bg-green-100 text-green-600',
  payment_due: 'bg-red-100 text-red-600',
  complaint_filed: 'bg-amber-100 text-amber-600',
  complaint_resolved: 'bg-green-100 text-green-600',
  agreement_signed: 'bg-teal-100 text-teal-600',
  system: 'bg-slate-100 text-slate-600',
};

// ── Module-level chime state ────────────────────────────────────────────────
// The bell lives inside <Sidebar/>, which every page renders individually, so
// the component REMOUNTS on each navigation. Keeping the last-seen count in a
// ref reset it on every route change — the "no chime on first poll" guard then
// swallowed every poll and the chime could never fire. Module scope survives
// remounts (per-tab, cleared on hard reload — acceptable: a reload IS a fresh
// baseline). Keyed by user id so a logout/login on a shared browser doesn't
// chime for the previous user's backlog.
let lastSeen = { userId: null, count: null };

// One shared AudioContext, created/resumed on the first user gesture so the
// browser's autoplay policy is satisfied by the time a chime is due. A fresh
// context per chime was (a) suspended on gesture-less tabs and (b) leaked
// toward Chrome's ~6-context cap when onended didn't fire.
let sharedCtx = null;
const ensureAudioContext = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedCtx || sharedCtx.state === 'closed') sharedCtx = new Ctx();
  if (sharedCtx.state === 'suspended') sharedCtx.resume().catch(() => {});
  return sharedCtx;
};
if (typeof document !== 'undefined') {
  // Prime on the first gesture anywhere in the app; keep listening (passive,
  // cheap) so a context suspended later (e.g. by the browser) gets resumed.
  document.addEventListener('pointerdown', ensureAudioContext, { passive: true });
  document.addEventListener('keydown', ensureAudioContext, { passive: true });
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(false);          // red bell pulse on new
  const [expandedId, setExpandedId] = useState(null);  // inline-expanded item
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;

  // Where a notification click should go for THIS role — see utils/notificationLink.
  // Returns null to inline-expand. Shared with the "See all" page so the two
  // surfaces resolve links identically.
  const linkForRole = (n) => notificationTarget(n, role);

  // Field-drift tolerant read check: some notifications were written with
  // `read`, others with `is_read`. Treat either truthy value as read.
  const isRead = (n) => n.is_read || n.read;

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 30 seconds. Also refetch the moment
    // the tab becomes visible again — background-tab throttling stretches
    // intervals, so returning users otherwise stare at a stale badge.
    const interval = setInterval(fetchUnreadCount, 30000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Short chime on the SHARED AudioContext (primed by the first user gesture —
  // see ensureAudioContext above). If the tab genuinely has no gesture yet the
  // context stays suspended and the chime is silent — non-fatal by design.
  const playChime = () => {
    try {
      const ctx = ensureAudioContext();
      if (!ctx || ctx.state !== 'running') return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      osc.start();
      osc.stop(ctx.currentTime + 0.42);
    } catch (e) { /* audio unavailable — ignore */ }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await notificationAPI.getUnreadCount();
      const next = res.data.count;
      setUnreadCount(next);
      // Chime + pulse only when the count GOES UP for THIS user (new work
      // arrived) — not on the first poll of a session, and not when the count
      // drops as items are read. lastSeen lives at module scope so page
      // navigation (which remounts the bell) doesn't reset the baseline.
      const sameUser = lastSeen.userId === user?.id;
      if (sameUser && lastSeen.count != null && next > lastSeen.count) {
        playChime();
        setPulse(true);
        setTimeout(() => setPulse(false), 2500);
      }
      lastSeen = { userId: user?.id, count: next };
    } catch (error) {
      console.log('Could not fetch notification count');
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.getNotifications();
      setNotifications(res.data);
    } catch (error) {
      console.log('Could not fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!isRead(notification)) {
      try {
        await notificationAPI.markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? {...n, is_read: true, read: true} : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        // Keep the chime baseline in step with what the user just read, so a
        // poll landing in this window can't see the count "rise" back to the
        // server value and chime for something already dismissed.
        if (lastSeen.count != null) {
          lastSeen = { ...lastSeen, count: Math.max(0, lastSeen.count - 1) };
        }
      } catch (error) {
        console.log('Could not mark notification as read');
      }
    }

    const target = linkForRole(notification);
    if (target) {
      setIsOpen(false);
      navigate(target);
    } else {
      // No destination to follow — expand inline so the full message is readable
      // (previously a linkless notification did nothing and the text was
      // truncated to a half-cut line).
      setExpandedId(prev => prev === notification.id ? null : notification.id);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({...n, is_read: true, read: true})));
      setUnreadCount(0);
      lastSeen = { ...lastSeen, count: 0 };
    } catch (error) {
      console.log('Could not mark all as read');
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationAPI.clearAll();
      setNotifications([]);
      setUnreadCount(0);
      lastSeen = { ...lastSeen, count: 0 };
    } catch (error) {
      console.log('Could not clear notifications');
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleToggle}
        className={`relative p-2 rounded-lg hover:bg-slate-100 transition-colors ${pulse ? 'ring-2 ring-red-400 animate-pulse' : ''}`}
        data-testid="notification-bell"
      >
        <Bell size={22} className={pulse ? 'text-red-500' : 'text-slate-600'} />
        {unreadCount > 0 && (
          <span className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center ${pulse ? 'animate-pulse' : ''}`}>
            {unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <>
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                    title="Mark all as read"
                  >
                    <Check size={14} />
                    Mark all read
                  </button>
                  <button
                    onClick={handleClearAll}
                    className="text-xs text-slate-500 hover:text-red-600 font-medium flex items-center gap-1"
                    title="Clear all"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-slate-200 rounded"
              >
                <X size={16} className="text-slate-500" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                const colorClass = NOTIFICATION_COLORS[notification.type] || 'bg-slate-100 text-slate-600';
                
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${
                      !isRead(notification) ? 'bg-blue-50/50' : ''
                    }`}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!isRead(notification) ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                            {notification.title}
                          </p>
                          {!isRead(notification) && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
                          )}
                        </div>
                        <p className={`text-sm text-slate-500 ${expandedId === notification.id ? 'whitespace-pre-wrap' : 'line-clamp-2'}`}>{notification.message}</p>
                        {!linkForRole(notification) && (
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {expandedId === notification.id ? 'Click to collapse' : 'Click to read more'}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{formatTime(notification.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-center">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
